/**
 * A linear, readable outline of the flow.
 *
 * This is the accessible counterpart to the canvas: the same information a
 * sighted user gets from the picture, as a nested list a screen reader can walk
 * and a keyboard user can navigate. It is derived from the graph rather than
 * the AST so that what it describes is exactly what is drawn — including
 * inlined function bodies and the edges the compiler synthesised.
 */
import type { FlowGraph, GraphEdge, GraphNode, NodeRole } from '../graph/model/types';

export interface OutlineEntry {
  readonly nodeId: string;
  readonly label: string;
  readonly role: NodeRole;
  /** Indentation level, from branch nesting. */
  readonly depth: number;
  /** How this node was reached, e.g. `Yes`, `No`, `case "Admin"`. */
  readonly viaLabel: string;
  /** Human description of where control goes next. */
  readonly outgoing: readonly OutlineTransition[];
  /** True when nothing points at this node — unreachable, or a separate flow. */
  readonly isEntry: boolean;
  /** True when control stops here. */
  readonly isExit: boolean;
  readonly groupLabel: string | null;
}

export interface OutlineTransition {
  readonly targetId: string;
  readonly targetLabel: string;
  readonly label: string;
  readonly isBackEdge: boolean;
}

export interface Outline {
  readonly entries: readonly OutlineEntry[];
  readonly nodeCount: number;
  readonly edgeCount: number;
  readonly decisionCount: number;
  readonly loopCount: number;
}

/**
 * Depth-first walk from every entry point, in compile order.
 *
 * A node is emitted the first time it is reached; later arrivals show up as
 * transitions pointing back at it. That keeps convergence points from being
 * repeated, which is what makes the list readable at all.
 */
export function buildOutline(graph: FlowGraph): Outline {
  const nodesById = new Map(graph.nodes.map((node) => [node.id, node]));
  const groupsByNode = new Map<string, string>();
  for (const group of graph.groups) {
    for (const id of group.nodeIds) groupsByNode.set(id, group.label);
  }

  const outgoingByNode = new Map<string, GraphEdge[]>();
  for (const edge of graph.edges) {
    const list = outgoingByNode.get(edge.source);
    if (list) list.push(edge);
    else outgoingByNode.set(edge.source, [edge]);
  }

  const entries: OutlineEntry[] = [];
  const visited = new Set<string>();

  const describe = (node: GraphNode, depth: number, viaLabel: string): void => {
    const outgoing = (outgoingByNode.get(node.id) ?? []).map((edge) => ({
      targetId: edge.target,
      targetLabel: nodesById.get(edge.target)?.label ?? edge.target,
      label: edge.label,
      isBackEdge: edge.isBackEdge,
    }));

    entries.push({
      nodeId: node.id,
      label: node.label,
      role: node.role,
      depth,
      viaLabel,
      outgoing,
      isEntry: node.incoming.length === 0,
      isExit: outgoing.length === 0,
      groupLabel: groupsByNode.get(node.id) ?? null,
    });
  };

  const walk = (nodeId: string, depth: number, viaLabel: string): void => {
    if (visited.has(nodeId)) return;
    const node = nodesById.get(nodeId);
    if (!node) return;
    visited.add(nodeId);
    describe(node, depth, viaLabel);

    const outgoing = outgoingByNode.get(nodeId) ?? [];
    // A branch point indents its children; a straight line does not.
    const childDepth = outgoing.length > 1 ? depth + 1 : depth;
    for (const edge of outgoing) {
      if (edge.isBackEdge) continue; // already described where it loops to
      walk(edge.target, childDepth, edge.label);
    }
  };

  // Entry points first, in compile order, then anything left over so nothing
  // is silently dropped from the outline.
  for (const node of graph.nodes) {
    if (node.incoming.length === 0) walk(node.id, 0, '');
  }
  for (const node of graph.nodes) {
    walk(node.id, 0, '');
  }

  return {
    entries,
    nodeCount: graph.nodes.length,
    edgeCount: graph.edges.length,
    decisionCount: graph.nodes.filter((n) => n.role === 'decision').length,
    loopCount: graph.nodes.filter((n) => n.role === 'loop').length,
  };
}

/** One-line summary used for the live region announcement after an update. */
export function summarizeGraph(graph: FlowGraph): {
  nodes: number;
  edges: number;
  decisions: number;
  loops: number;
} {
  return {
    nodes: graph.nodes.length,
    edges: graph.edges.length,
    decisions: graph.nodes.filter((n) => n.role === 'decision').length,
    loops: graph.nodes.filter((n) => n.role === 'loop').length,
  };
}
